"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkTypeMatch = checkTypeMatch;
exports.checkValueTypeMatch = checkValueTypeMatch;
const uuid_1 = require("uuid");
const api_endpoints_1 = require("../api-endpoints");
const application_logger_1 = require("../application-logger");
const attributes_1 = require("../attributes");
const bandit_evaluator_1 = require("../bandit-evaluator");
const lru_in_memory_assignment_cache_1 = require("../cache/lru-in-memory-assignment-cache");
const non_expiring_in_memory_cache_assignment_1 = require("../cache/non-expiring-in-memory-cache-assignment");
const tlru_in_memory_assignment_cache_1 = require("../cache/tlru-in-memory-assignment-cache");
const configuration_requestor_1 = require("../configuration-requestor");
const memory_store_1 = require("../configuration-store/memory.store");
const configuration_wire_types_1 = require("../configuration-wire-types");
const constants_1 = require("../constants");
const decoding_1 = require("../decoding");
const eppo_value_1 = require("../eppo_value");
const evaluator_1 = require("../evaluator");
const bounded_event_queue_1 = require("../events/bounded-event-queue");
const no_op_event_dispatcher_1 = require("../events/no-op-event-dispatcher");
const flag_evaluation_details_builder_1 = require("../flag-evaluation-details-builder");
const flag_evaluation_error_1 = require("../flag-evaluation-error");
const http_client_1 = require("../http-client");
const i_configuration_1 = require("../i-configuration");
const interfaces_1 = require("../interfaces");
const obfuscation_1 = require("../obfuscation");
const override_validator_1 = require("../override-validator");
const poller_1 = require("../poller");
const util_1 = require("../util");
const validation_1 = require("../validation");
const version_1 = require("../version");
class EppoClient {
    constructor({ eventDispatcher = new no_op_event_dispatcher_1.default(), isObfuscated = false, flagConfigurationStore, banditVariationConfigurationStore, banditModelConfigurationStore, overrideStore, configurationRequestParameters, }) {
        this.assignmentEventsQueue = new bounded_event_queue_1.BoundedEventQueue('assignments');
        this.banditEventsQueue = new bounded_event_queue_1.BoundedEventQueue('bandit');
        this.banditEvaluator = new bandit_evaluator_1.BanditEvaluator();
        // whether to suppress any errors and return default values instead
        this.isGracefulFailureMode = true;
        this.obfuscationMismatchWarningIssued = false;
        this.evaluator = new evaluator_1.Evaluator();
        this.overrideValidator = new override_validator_1.OverrideValidator();
        this.eventDispatcher = eventDispatcher;
        this.flagConfigurationStore = flagConfigurationStore;
        this.banditVariationConfigurationStore = banditVariationConfigurationStore;
        this.banditModelConfigurationStore = banditModelConfigurationStore;
        this.overrideStore = overrideStore;
        this.configurationRequestParameters = configurationRequestParameters;
        this.expectObfuscated = isObfuscated;
    }
    getConfiguration() {
        return this.configurationRequestor
            ? this.configurationRequestor.getConfiguration()
            : new i_configuration_1.StoreBackedConfiguration(this.flagConfigurationStore, this.banditVariationConfigurationStore, this.banditModelConfigurationStore);
    }
    maybeWarnAboutObfuscationMismatch(configObfuscated) {
        // Don't warn again if we did on the last check.
        if (configObfuscated !== this.expectObfuscated && !this.obfuscationMismatchWarningIssued) {
            this.obfuscationMismatchWarningIssued = true;
            application_logger_1.logger.warn(`[Eppo SDK] configuration obfuscation [${configObfuscated}] does not match expected [${this.expectObfuscated}]`);
        }
        else if (configObfuscated === this.expectObfuscated) {
            // Reset the warning to false in case the client configuration (re-)enters a mismatched state.
            this.obfuscationMismatchWarningIssued = false;
        }
    }
    /**
     * This method delegates to the configuration to determine whether  it is obfuscated, then caches the actual
     * obfuscation state and issues a warning if it hasn't already.
     * This method can be removed with the next major update when the @deprecated setIsObfuscated is removed
     */
    isObfuscated(config) {
        if (this.configObfuscatedCache === undefined) {
            this.configObfuscatedCache = config.isObfuscated();
        }
        this.maybeWarnAboutObfuscationMismatch(this.configObfuscatedCache);
        return this.configObfuscatedCache;
    }
    /**
     * Validates and parses x-eppo-overrides header sent by Eppo's Chrome extension
     */
    async parseOverrides(overridePayload) {
        if (!overridePayload) {
            return undefined;
        }
        const payload = this.overrideValidator.parseOverridePayload(overridePayload);
        await this.overrideValidator.validateKey(payload.browserExtensionKey);
        return payload.overrides;
    }
    /**
     * Creates an EppoClient instance that has the specified overrides applied
     * to it without affecting the original EppoClient singleton. Useful for
     * applying overrides in a shared Node instance, such as a web server.
     */
    withOverrides(overrides) {
        if (overrides && Object.keys(overrides).length) {
            const copy = (0, util_1.shallowClone)(this);
            copy.overrideStore = new memory_store_1.MemoryOnlyConfigurationStore();
            copy.overrideStore.setEntries(overrides);
            return copy;
        }
        return this;
    }
    setConfigurationRequestParameters(configurationRequestParameters) {
        this.configurationRequestParameters = configurationRequestParameters;
    }
    // noinspection JSUnusedGlobalSymbols
    setFlagConfigurationStore(flagConfigurationStore) {
        this.flagConfigurationStore = flagConfigurationStore;
        this.configObfuscatedCache = undefined;
    }
    // noinspection JSUnusedGlobalSymbols
    setBanditVariationConfigurationStore(banditVariationConfigurationStore) {
        this.banditVariationConfigurationStore = banditVariationConfigurationStore;
    }
    /** Sets the EventDispatcher instance to use when tracking events with {@link track}. */
    // noinspection JSUnusedGlobalSymbols
    setEventDispatcher(eventDispatcher) {
        this.eventDispatcher = eventDispatcher;
    }
    /**
     * Attaches a context to be included with all events dispatched by the EventDispatcher.
     * The context is delivered as a top-level object in the ingestion request payload.
     * An existing key can be removed by providing a `null` value.
     * Calling this method with same key multiple times causes only the last value to be used for the
     * given key.
     *
     * @param key - The context entry key.
     * @param value - The context entry value, must be a string, number, boolean, or null. If value is
     * an object or an array, will throw an ArgumentError.
     */
    setContext(key, value) {
        this.eventDispatcher?.attachContext(key, value);
    }
    // noinspection JSUnusedGlobalSymbols
    setBanditModelConfigurationStore(banditModelConfigurationStore) {
        this.banditModelConfigurationStore = banditModelConfigurationStore;
    }
    // noinspection JSUnusedGlobalSymbols
    /**
     * Setting this value will have no side effects other than triggering a warning when the actual
     * configuration's obfuscated does not match the value set here.
     *
     * @deprecated The client determines whether the configuration is obfuscated by inspection
     * @param isObfuscated
     */
    setIsObfuscated(isObfuscated) {
        this.expectObfuscated = isObfuscated;
    }
    setOverrideStore(store) {
        this.overrideStore = store;
    }
    unsetOverrideStore() {
        this.overrideStore = undefined;
    }
    // Returns a mapping of flag key to variation key for all active overrides
    getOverrideVariationKeys() {
        return Object.fromEntries(Object.entries(this.overrideStore?.entries() ?? {}).map(([flagKey, value]) => [
            flagKey,
            value.key,
        ]));
    }
    async fetchFlagConfigurations() {
        if (!this.configurationRequestParameters) {
            throw new Error('Eppo SDK unable to fetch flag configurations without configuration request parameters');
        }
        // if fetchFlagConfigurations() was previously called, stop any polling process from that call
        this.requestPoller?.stop();
        const { apiKey, sdkName, sdkVersion, baseUrl, // Default is set in ApiEndpoints constructor if undefined
        requestTimeoutMs = constants_1.DEFAULT_REQUEST_TIMEOUT_MS, numInitialRequestRetries = constants_1.DEFAULT_INITIAL_CONFIG_REQUEST_RETRIES, numPollRequestRetries = constants_1.DEFAULT_POLL_CONFIG_REQUEST_RETRIES, pollAfterSuccessfulInitialization = false, pollAfterFailedInitialization = false, throwOnFailedInitialization = false, skipInitialPoll = false, } = this.configurationRequestParameters;
        let { pollingIntervalMs = constants_1.DEFAULT_POLL_INTERVAL_MS } = this.configurationRequestParameters;
        if (pollingIntervalMs <= 0) {
            application_logger_1.logger.error('pollingIntervalMs must be greater than 0. Using default');
            pollingIntervalMs = constants_1.DEFAULT_POLL_INTERVAL_MS;
        }
        // todo: Inject the chain of dependencies below
        const apiEndpoints = new api_endpoints_1.default({
            baseUrl,
            queryParams: { apiKey, sdkName, sdkVersion },
        });
        const httpClient = new http_client_1.default(apiEndpoints, requestTimeoutMs);
        const configurationRequestor = new configuration_requestor_1.default(httpClient, this.flagConfigurationStore, this.banditVariationConfigurationStore ?? null, this.banditModelConfigurationStore ?? null);
        const pollingCallback = async () => {
            if (await configurationRequestor.isFlagConfigExpired()) {
                this.configObfuscatedCache = undefined;
                return configurationRequestor.fetchAndStoreConfigurations();
            }
        };
        this.requestPoller = (0, poller_1.default)(pollingIntervalMs, pollingCallback, {
            maxStartRetries: numInitialRequestRetries,
            maxPollRetries: numPollRequestRetries,
            pollAfterSuccessfulStart: pollAfterSuccessfulInitialization,
            pollAfterFailedStart: pollAfterFailedInitialization,
            errorOnFailedStart: throwOnFailedInitialization,
            skipInitialPoll: skipInitialPoll,
        });
        await this.requestPoller.start();
    }
    // noinspection JSUnusedGlobalSymbols
    stopPolling() {
        if (this.requestPoller) {
            this.requestPoller.stop();
        }
    }
    /**
     * Maps a subject to a string variation for a given experiment.
     *
     * @param flagKey feature flag identifier
     * @param subjectKey an identifier of the experiment subject, for example a user ID.
     * @param subjectAttributes optional attributes associated with the subject, for example name and email.
     * @param defaultValue default value to return if the subject is not part of the experiment sample
     * The subject attributes are used for evaluating any targeting rules tied to the experiment.
     * @returns a variation value if the subject is part of the experiment sample, otherwise the default value
     * @public
     */
    getStringAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        return this.getStringAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue)
            .variation;
    }
    /**
     * Maps a subject to a string variation for a given experiment and provides additional details about the
     * variation assigned and the reason for the assignment.
     *
     * @param flagKey feature flag identifier
     * @param subjectKey an identifier of the experiment subject, for example a user ID.
     * @param subjectAttributes optional attributes associated with the subject, for example name and email.
     * @param defaultValue default value to return if the subject is not part of the experiment sample
     * The subject attributes are used for evaluating any targeting rules tied to the experiment.
     * @returns an object that includes the variation value along with additional metadata about the assignment
     * @public
     */
    getStringAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue) {
        const { eppoValue, flagEvaluationDetails } = this.getAssignmentVariation(flagKey, subjectKey, subjectAttributes, eppo_value_1.EppoValue.String(defaultValue), interfaces_1.VariationType.STRING);
        return {
            variation: eppoValue.stringValue ?? defaultValue,
            action: null,
            evaluationDetails: flagEvaluationDetails,
        };
    }
    /**
     * @deprecated use getBooleanAssignment instead.
     */
    getBoolAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        return this.getBooleanAssignment(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    /**
     * Maps a subject to a boolean variation for a given experiment.
     *
     * @param flagKey feature flag identifier
     * @param subjectKey an identifier of the experiment subject, for example a user ID.
     * @param subjectAttributes optional attributes associated with the subject, for example name and email.
     * @param defaultValue default value to return if the subject is not part of the experiment sample
     * @returns a boolean variation value if the subject is part of the experiment sample, otherwise the default value
     */
    getBooleanAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        return this.getBooleanAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue)
            .variation;
    }
    /**
     * Maps a subject to a boolean variation for a given experiment and provides additional details about the
     * variation assigned and the reason for the assignment.
     *
     * @param flagKey feature flag identifier
     * @param subjectKey an identifier of the experiment subject, for example a user ID.
     * @param subjectAttributes optional attributes associated with the subject, for example name and email.
     * @param defaultValue default value to return if the subject is not part of the experiment sample
     * The subject attributes are used for evaluating any targeting rules tied to the experiment.
     * @returns an object that includes the variation value along with additional metadata about the assignment
     * @public
     */
    getBooleanAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue) {
        const { eppoValue, flagEvaluationDetails } = this.getAssignmentVariation(flagKey, subjectKey, subjectAttributes, eppo_value_1.EppoValue.Bool(defaultValue), interfaces_1.VariationType.BOOLEAN);
        return {
            variation: eppoValue.boolValue ?? defaultValue,
            action: null,
            evaluationDetails: flagEvaluationDetails,
        };
    }
    /**
     * Maps a subject to an Integer variation for a given experiment.
     *
     * @param flagKey feature flag identifier
     * @param subjectKey an identifier of the experiment subject, for example a user ID.
     * @param subjectAttributes optional attributes associated with the subject, for example name and email.
     * @param defaultValue default value to return if the subject is not part of the experiment sample
     * @returns an integer variation value if the subject is part of the experiment sample, otherwise the default value
     */
    getIntegerAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        return this.getIntegerAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue)
            .variation;
    }
    /**
     * Maps a subject to an Integer variation for a given experiment and provides additional details about the
     * variation assigned and the reason for the assignment.
     *
     * @param flagKey feature flag identifier
     * @param subjectKey an identifier of the experiment subject, for example a user ID.
     * @param subjectAttributes optional attributes associated with the subject, for example name and email.
     * @param defaultValue default value to return if the subject is not part of the experiment sample
     * The subject attributes are used for evaluating any targeting rules tied to the experiment.
     * @returns an object that includes the variation value along with additional metadata about the assignment
     * @public
     */
    getIntegerAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue) {
        const { eppoValue, flagEvaluationDetails } = this.getAssignmentVariation(flagKey, subjectKey, subjectAttributes, eppo_value_1.EppoValue.Numeric(defaultValue), interfaces_1.VariationType.INTEGER);
        return {
            variation: eppoValue.numericValue ?? defaultValue,
            action: null,
            evaluationDetails: flagEvaluationDetails,
        };
    }
    /**
     * Maps a subject to a numeric variation for a given experiment.
     *
     * @param flagKey feature flag identifier
     * @param subjectKey an identifier of the experiment subject, for example a user ID.
     * @param subjectAttributes optional attributes associated with the subject, for example name and email.
     * @param defaultValue default value to return if the subject is not part of the experiment sample
     * @returns a number variation value if the subject is part of the experiment sample, otherwise the default value
     */
    getNumericAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        return this.getNumericAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue)
            .variation;
    }
    /**
     * Maps a subject to a numeric variation for a given experiment and provides additional details about the
     * variation assigned and the reason for the assignment.
     *
     * @param flagKey feature flag identifier
     * @param subjectKey an identifier of the experiment subject, for example a user ID.
     * @param subjectAttributes optional attributes associated with the subject, for example name and email.
     * @param defaultValue default value to return if the subject is not part of the experiment sample
     * The subject attributes are used for evaluating any targeting rules tied to the experiment.
     * @returns an object that includes the variation value along with additional metadata about the assignment
     * @public
     */
    getNumericAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue) {
        const { eppoValue, flagEvaluationDetails } = this.getAssignmentVariation(flagKey, subjectKey, subjectAttributes, eppo_value_1.EppoValue.Numeric(defaultValue), interfaces_1.VariationType.NUMERIC);
        return {
            variation: eppoValue.numericValue ?? defaultValue,
            action: null,
            evaluationDetails: flagEvaluationDetails,
        };
    }
    /**
     * Maps a subject to a JSON variation for a given experiment.
     *
     * @param flagKey feature flag identifier
     * @param subjectKey an identifier of the experiment subject, for example a user ID.
     * @param subjectAttributes optional attributes associated with the subject, for example name and email.
     * @param defaultValue default value to return if the subject is not part of the experiment sample
     * @returns a JSON object variation value if the subject is part of the experiment sample, otherwise the default value
     */
    getJSONAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        return this.getJSONAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue)
            .variation;
    }
    getJSONAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue) {
        const { eppoValue, flagEvaluationDetails } = this.getAssignmentVariation(flagKey, subjectKey, subjectAttributes, eppo_value_1.EppoValue.JSON(defaultValue), interfaces_1.VariationType.JSON);
        return {
            variation: eppoValue.objectValue ?? defaultValue,
            action: null,
            evaluationDetails: flagEvaluationDetails,
        };
    }
    getBanditAction(flagKey, subjectKey, subjectAttributes, actions, defaultValue) {
        const { variation, action } = this.getBanditActionDetails(flagKey, subjectKey, subjectAttributes, actions, defaultValue);
        return { variation, action };
    }
    /**
     * Evaluates the supplied actions using the first bandit associated with `flagKey` and returns the best ranked action.
     *
     * This method should be considered **preview** and is subject to change as requirements mature.
     *
     * NOTE: This method does not do any logging or assignment computation and so calling this method will have
     * NO IMPACT on bandit and experiment training.
     *
     * Only use this method under certain circumstances (i.e. where the impact of the choice of bandit cannot be measured,
     * but you want to put the "best foot forward", for example, when being web-crawled).
     *
     */
    getBestAction(flagKey, subjectAttributes, actions, defaultAction) {
        const config = this.getConfiguration();
        let result = null;
        const flagBanditVariations = config.getFlagBanditVariations(flagKey);
        const banditKey = flagBanditVariations?.at(0)?.key;
        if (banditKey) {
            const banditParameters = config.getBandit(banditKey);
            if (banditParameters) {
                const contextualSubjectAttributes = (0, attributes_1.ensureContextualSubjectAttributes)(subjectAttributes);
                const actionsWithContextualAttributes = (0, attributes_1.ensureActionsWithContextualAttributes)(actions);
                result = this.banditEvaluator.evaluateBestBanditAction(contextualSubjectAttributes, actionsWithContextualAttributes, banditParameters.modelData);
            }
        }
        return result ?? defaultAction;
    }
    getBanditActionDetails(flagKey, subjectKey, subjectAttributes, actions, defaultValue) {
        const config = this.getConfiguration();
        let variation = defaultValue;
        let action = null;
        // Initialize with a generic evaluation details. This will mutate as the function progresses.
        let evaluationDetails = this.newFlagEvaluationDetailsBuilder(config, flagKey).buildForNoneResult('ASSIGNMENT_ERROR', 'Unexpected error getting assigned variation for bandit action');
        try {
            // Get the assigned variation for the flag with a possible bandit
            // Note for getting assignments, we don't care about context
            const nonContextualSubjectAttributes = (0, attributes_1.ensureNonContextualSubjectAttributes)(subjectAttributes);
            const { variation: assignedVariation, evaluationDetails: assignmentEvaluationDetails } = this.getStringAssignmentDetails(flagKey, subjectKey, nonContextualSubjectAttributes, defaultValue);
            variation = assignedVariation;
            evaluationDetails = assignmentEvaluationDetails;
            // Check if the assigned variation is an active bandit
            // Note: the reason for non-bandit assignments include the subject being bucketed into a non-bandit variation or
            // a rollout having been done.
            const bandit = config.getFlagVariationBandit(flagKey, variation);
            if (!bandit) {
                return { variation, action: null, evaluationDetails };
            }
            evaluationDetails.banditKey = bandit.banditKey;
            const banditEvaluation = this.evaluateBanditAction(flagKey, subjectKey, subjectAttributes, actions, bandit.modelData);
            if (banditEvaluation?.actionKey) {
                action = banditEvaluation.actionKey;
                const banditEvent = {
                    timestamp: new Date().toISOString(),
                    featureFlag: flagKey,
                    bandit: bandit.banditKey,
                    subject: subjectKey,
                    action,
                    actionProbability: banditEvaluation.actionWeight,
                    optimalityGap: banditEvaluation.optimalityGap,
                    modelVersion: bandit.modelVersion,
                    subjectNumericAttributes: banditEvaluation.subjectAttributes.numericAttributes,
                    subjectCategoricalAttributes: banditEvaluation.subjectAttributes.categoricalAttributes,
                    actionNumericAttributes: banditEvaluation.actionAttributes.numericAttributes,
                    actionCategoricalAttributes: banditEvaluation.actionAttributes.categoricalAttributes,
                    metaData: this.buildLoggerMetadata(),
                    evaluationDetails,
                };
                try {
                    this.logBanditAction(banditEvent);
                }
                catch (err) {
                    application_logger_1.logger.error('Error logging bandit event', err);
                }
                evaluationDetails.banditAction = action;
            }
        }
        catch (err) {
            application_logger_1.logger.error('Error determining bandit action', err);
            if (!this.isGracefulFailureMode) {
                throw err;
            }
            if (variation) {
                // If we have a variation, the assignment succeeded and the error was with the bandit part.
                // Update the flag evaluation code to indicate that
                evaluationDetails.flagEvaluationCode = 'BANDIT_ERROR';
            }
            evaluationDetails.flagEvaluationDescription = `Error evaluating bandit action: ${err.message}`;
        }
        return { variation, action, evaluationDetails };
    }
    /**
     * For use with 3rd party CMS tooling, such as the Contentful Eppo plugin.
     *
     * CMS plugins that integrate with Eppo will follow a common format for
     * creating a feature flag. The flag created by the CMS plugin will have
     * variations with values 'control', 'treatment-1', 'treatment-2', etc.
     * This function allows users to easily return the CMS container entry
     * for the assigned variation.
     *
     * @param flagExperiment the flag key, control container entry and treatment container entries.
     * @param subjectKey an identifier of the experiment subject, for example a user ID.
     * @param subjectAttributes optional attributes associated with the subject, for example name and email.
     * @returns The container entry associated with the experiment.
     */
    getExperimentContainerEntry(flagExperiment, subjectKey, subjectAttributes) {
        const { flagKey, controlVariationEntry, treatmentVariationEntries } = flagExperiment;
        const assignment = this.getStringAssignment(flagKey, subjectKey, subjectAttributes, 'control');
        if (assignment === 'control') {
            return controlVariationEntry;
        }
        if (!assignment.startsWith('treatment-')) {
            application_logger_1.logger.warn(`Variation '${assignment}' cannot be mapped to a container. Defaulting to control variation.`);
            return controlVariationEntry;
        }
        const treatmentVariationIndex = Number.parseInt(assignment.split('-')[1]) - 1;
        if (isNaN(treatmentVariationIndex)) {
            application_logger_1.logger.warn(`Variation '${assignment}' cannot be mapped to a container. Defaulting to control variation.`);
            return controlVariationEntry;
        }
        if (treatmentVariationIndex >= treatmentVariationEntries.length) {
            application_logger_1.logger.warn(`Selected treatment variation (${treatmentVariationIndex}) index is out of bounds. Defaulting to control variation.`);
            return controlVariationEntry;
        }
        return treatmentVariationEntries[treatmentVariationIndex];
    }
    evaluateBanditAction(flagKey, subjectKey, subjectAttributes, actions, banditModelData) {
        // If no actions, there is nothing to do
        if (!Object.keys(actions).length) {
            return null;
        }
        const contextualSubjectAttributes = (0, attributes_1.ensureContextualSubjectAttributes)(subjectAttributes);
        const actionsWithContextualAttributes = (0, attributes_1.ensureActionsWithContextualAttributes)(actions);
        return this.banditEvaluator.evaluateBandit(flagKey, subjectKey, contextualSubjectAttributes, actionsWithContextualAttributes, banditModelData);
    }
    logBanditAction(banditEvent) {
        // First we check if this bandit action has been logged before
        const subjectKey = banditEvent.subject;
        const flagKey = banditEvent.featureFlag;
        const banditKey = banditEvent.bandit;
        const actionKey = banditEvent.action ?? '__eppo_no_action';
        const banditAssignmentCacheProperties = {
            flagKey,
            subjectKey,
            banditKey,
            actionKey,
        };
        if (this.banditAssignmentCache?.has(banditAssignmentCacheProperties)) {
            // Ignore repeat assignment
            return;
        }
        // If here, we have a logger and a new assignment to be logged
        try {
            if (this.banditLogger) {
                this.banditLogger.logBanditAction(banditEvent);
            }
            else {
                // If no logger defined, queue up the events (up to a max) to flush if a logger is later defined
                this.banditEventsQueue.push(banditEvent);
            }
            // Record in the assignment cache, if active, to deduplicate subsequent repeat assignments
            this.banditAssignmentCache?.set(banditAssignmentCacheProperties);
        }
        catch (err) {
            application_logger_1.logger.warn('Error encountered logging bandit action', err);
        }
    }
    getAssignmentVariation(flagKey, subjectKey, subjectAttributes, defaultValue, expectedVariationType) {
        try {
            const result = this.getAssignmentDetail(flagKey, subjectKey, subjectAttributes, expectedVariationType);
            return this.parseVariationWithDetails(result, defaultValue, expectedVariationType);
        }
        catch (error) {
            const eppoValue = this.rethrowIfNotGraceful(error, defaultValue);
            if (error instanceof flag_evaluation_error_1.FlagEvaluationError && error.flagEvaluationDetails) {
                return {
                    eppoValue,
                    flagEvaluationDetails: error.flagEvaluationDetails,
                };
            }
            else {
                const flagEvaluationDetails = new flag_evaluation_details_builder_1.FlagEvaluationDetailsBuilder('', [], '', '').buildForNoneResult('ASSIGNMENT_ERROR', `Assignment Error: ${error.message}`);
                return {
                    eppoValue,
                    flagEvaluationDetails,
                };
            }
        }
    }
    parseVariationWithDetails({ flagEvaluationDetails, variation }, defaultValue, expectedVariationType) {
        try {
            if (!variation || flagEvaluationDetails.flagEvaluationCode !== 'MATCH') {
                return { eppoValue: defaultValue, flagEvaluationDetails };
            }
            const eppoValue = eppo_value_1.EppoValue.valueOf(variation.value, expectedVariationType);
            return { eppoValue, flagEvaluationDetails };
        }
        catch (error) {
            const eppoValue = this.rethrowIfNotGraceful(error, defaultValue);
            return { eppoValue, flagEvaluationDetails };
        }
    }
    rethrowIfNotGraceful(err, defaultValue) {
        if (this.isGracefulFailureMode) {
            application_logger_1.logger.error(`${application_logger_1.loggerPrefix} Error getting assignment: ${err.message}`);
            return defaultValue ?? eppo_value_1.EppoValue.Null();
        }
        throw err;
    }
    getAllAssignments(subjectKey, subjectAttributes = {}) {
        const config = this.getConfiguration();
        const configDetails = config.getFlagConfigDetails();
        const flagKeys = this.getFlagKeys();
        const flags = {};
        // Evaluate all the enabled flags for the user
        flagKeys.forEach((flagKey) => {
            const flag = this.getNormalizedFlag(config, flagKey);
            if (!flag) {
                application_logger_1.logger.debug(`${application_logger_1.loggerPrefix} No assigned variation. Flag does not exist.`);
                return;
            }
            // Evaluate the flag for this subject.
            const evaluation = this.evaluator.evaluateFlag(flag, configDetails, subjectKey, subjectAttributes, this.isObfuscated(config));
            // allocationKey is set along with variation when there is a result. this check appeases typescript below
            if (!evaluation.variation || !evaluation.allocationKey) {
                application_logger_1.logger.debug(`${application_logger_1.loggerPrefix} No assigned variation: ${flagKey}`);
                return;
            }
            // Transform into a PrecomputedFlag
            flags[flagKey] = {
                flagKey,
                allocationKey: evaluation.allocationKey,
                doLog: evaluation.doLog,
                extraLogging: evaluation.extraLogging,
                variationKey: evaluation.variation.key,
                variationType: flag.variationType,
                variationValue: evaluation.variation.value.toString(),
            };
        });
        return flags;
    }
    /**
     * Computes and returns assignments and bandits for a subject from all loaded flags.
     *
     * @param subjectKey an identifier of the experiment subject, for example a user ID.
     * @param subjectAttributes optional attributes associated with the subject, for example name and email.
     * @param banditActions optional attributes associated with the bandit actions
     * @param salt a salt to use for obfuscation
     */
    getPrecomputedConfiguration(subjectKey, subjectAttributes = {}, banditActions = {}, salt) {
        const config = this.getConfiguration();
        const configDetails = config.getFlagConfigDetails();
        const subjectContextualAttributes = (0, attributes_1.ensureContextualSubjectAttributes)(subjectAttributes);
        const subjectFlatAttributes = (0, attributes_1.ensureNonContextualSubjectAttributes)(subjectAttributes);
        const flags = this.getAllAssignments(subjectKey, subjectFlatAttributes);
        const bandits = this.computeBanditsForFlags(config, subjectKey, subjectContextualAttributes, banditActions, flags);
        const precomputedConfig = configuration_wire_types_1.PrecomputedConfiguration.obfuscated(subjectKey, flags, bandits, salt ?? '', // no salt if not provided
        subjectContextualAttributes, configDetails.configEnvironment);
        const configWire = new configuration_wire_types_1.ConfigurationWireV1(precomputedConfig);
        return JSON.stringify(configWire);
    }
    /**
     * [Experimental] Get a detailed return of assignment for a particular subject and flag.
     *
     * Note: This method is experimental and may change in future versions.
     * Please only use for debugging purposes, and not in production.
     *
     * @param flagKey The flag key
     * @param subjectKey The subject key
     * @param subjectAttributes The subject attributes
     * @param expectedVariationType The expected variation type
     * @returns A detailed return of assignment for a particular subject and flag
     */
    getAssignmentDetail(flagKey, subjectKey, subjectAttributes = {}, expectedVariationType) {
        (0, validation_1.validateNotBlank)(subjectKey, 'Invalid argument: subjectKey cannot be blank');
        (0, validation_1.validateNotBlank)(flagKey, 'Invalid argument: flagKey cannot be blank');
        const config = this.getConfiguration();
        const flagEvaluationDetailsBuilder = this.newFlagEvaluationDetailsBuilder(config, flagKey);
        const overrideVariation = this.overrideStore?.get(flagKey);
        if (overrideVariation) {
            return (0, evaluator_1.overrideResult)(flagKey, subjectKey, subjectAttributes, overrideVariation, flagEvaluationDetailsBuilder);
        }
        const configDetails = config.getFlagConfigDetails();
        const flag = this.getNormalizedFlag(config, flagKey);
        if (flag === null) {
            application_logger_1.logger.warn(`${application_logger_1.loggerPrefix} No assigned variation. Flag not found: ${flagKey}`);
            // note: this is different from the Python SDK, which returns None instead
            const flagEvaluationDetails = flagEvaluationDetailsBuilder.buildForNoneResult('FLAG_UNRECOGNIZED_OR_DISABLED', `Unrecognized or disabled flag: ${flagKey}`);
            return (0, evaluator_1.noneResult)(flagKey, subjectKey, subjectAttributes, flagEvaluationDetails, configDetails.configFormat);
        }
        if (!checkTypeMatch(expectedVariationType, flag.variationType)) {
            const errorMessage = `Variation value does not have the correct type. Found ${flag.variationType}, but expected ${expectedVariationType} for flag ${flagKey}`;
            if (this.isGracefulFailureMode) {
                const flagEvaluationDetails = flagEvaluationDetailsBuilder.buildForNoneResult('TYPE_MISMATCH', errorMessage);
                return (0, evaluator_1.noneResult)(flagKey, subjectKey, subjectAttributes, flagEvaluationDetails, configDetails.configFormat);
            }
            throw new TypeError(errorMessage);
        }
        if (!flag.enabled) {
            application_logger_1.logger.info(`${application_logger_1.loggerPrefix} No assigned variation. Flag is disabled: ${flagKey}`);
            // note: this is different from the Python SDK, which returns None instead
            const flagEvaluationDetails = flagEvaluationDetailsBuilder.buildForNoneResult('FLAG_UNRECOGNIZED_OR_DISABLED', `Unrecognized or disabled flag: ${flagKey}`);
            return (0, evaluator_1.noneResult)(flagKey, subjectKey, subjectAttributes, flagEvaluationDetails, configDetails.configFormat);
        }
        const isObfuscated = this.isObfuscated(config);
        const result = this.evaluator.evaluateFlag(flag, configDetails, subjectKey, subjectAttributes, isObfuscated, expectedVariationType);
        if (isObfuscated) {
            // flag.key is obfuscated, replace with requested flag key
            result.flagKey = flagKey;
        }
        try {
            if (result?.doLog) {
                this.maybeLogAssignment(result);
            }
        }
        catch (error) {
            application_logger_1.logger.error(`${application_logger_1.loggerPrefix} Error logging assignment event: ${error}`);
        }
        return result;
    }
    /**
     * Enqueues an arbitrary event. Events must have a type and a payload.
     */
    track(type, payload) {
        this.eventDispatcher.dispatch({
            uuid: (0, uuid_1.v4)(),
            type,
            timestamp: new Date().getTime(),
            payload,
        });
    }
    newFlagEvaluationDetailsBuilder(config, flagKey) {
        const flag = this.getNormalizedFlag(config, flagKey);
        const configDetails = config.getFlagConfigDetails();
        return new flag_evaluation_details_builder_1.FlagEvaluationDetailsBuilder(configDetails.configEnvironment.name, flag?.allocations ?? [], configDetails.configFetchedAt, configDetails.configPublishedAt);
    }
    getNormalizedFlag(config, flagKey) {
        return this.isObfuscated(config)
            ? this.getObfuscatedFlag(config, flagKey)
            : config.getFlag(flagKey);
    }
    getObfuscatedFlag(config, flagKey) {
        const flag = config.getFlag((0, obfuscation_1.getMD5Hash)(flagKey));
        return flag ? (0, decoding_1.decodeFlag)(flag) : null;
    }
    // noinspection JSUnusedGlobalSymbols
    getFlagKeys() {
        /**
         * Returns a list of all flag keys that have been initialized.
         * This can be useful to debug the initialization process.
         *
         * Note that it is generally not a good idea to preload all flag configurations.
         */
        return this.getConfiguration().getFlagKeys();
    }
    isInitialized() {
        return this.getConfiguration().isInitialized();
    }
    /** @deprecated Use `setAssignmentLogger` */
    setLogger(logger) {
        this.setAssignmentLogger(logger);
    }
    setAssignmentLogger(logger) {
        this.assignmentLogger = logger;
        // log any assignment events that may have been queued while initializing
        this.flushQueuedEvents(this.assignmentEventsQueue, this.assignmentLogger?.logAssignment);
    }
    setBanditLogger(logger) {
        this.banditLogger = logger;
        // log any bandit events that may have been queued while initializing
        this.flushQueuedEvents(this.banditEventsQueue, this.banditLogger?.logBanditAction);
    }
    /**
     * Assignment cache methods.
     */
    disableAssignmentCache() {
        this.assignmentCache = undefined;
    }
    useNonExpiringInMemoryAssignmentCache() {
        this.assignmentCache = new non_expiring_in_memory_cache_assignment_1.NonExpiringInMemoryAssignmentCache();
    }
    useLRUInMemoryAssignmentCache(maxSize) {
        this.assignmentCache = new lru_in_memory_assignment_cache_1.LRUInMemoryAssignmentCache(maxSize);
    }
    // noinspection JSUnusedGlobalSymbols
    useCustomAssignmentCache(cache) {
        this.assignmentCache = cache;
    }
    disableBanditAssignmentCache() {
        this.banditAssignmentCache = undefined;
    }
    useNonExpiringInMemoryBanditAssignmentCache() {
        this.banditAssignmentCache = new non_expiring_in_memory_cache_assignment_1.NonExpiringInMemoryAssignmentCache();
    }
    /**
     * @param {number} maxSize - Maximum cache size
     * @param {number} timeout - TTL of cache entries
     */
    useExpiringInMemoryBanditAssignmentCache(maxSize, timeout) {
        this.banditAssignmentCache = new tlru_in_memory_assignment_cache_1.TLRUInMemoryAssignmentCache(maxSize, timeout);
    }
    // noinspection JSUnusedGlobalSymbols
    useCustomBanditAssignmentCache(cache) {
        this.banditAssignmentCache = cache;
    }
    setIsGracefulFailureMode(gracefulFailureMode) {
        this.isGracefulFailureMode = gracefulFailureMode;
    }
    getFlagConfigurations() {
        return this.getConfiguration().getFlags();
    }
    flushQueuedEvents(eventQueue, logFunction) {
        const eventsToFlush = eventQueue.flush();
        if (!logFunction) {
            return;
        }
        eventsToFlush.forEach((event) => {
            try {
                logFunction(event);
            }
            catch (error) {
                application_logger_1.logger.error(`${application_logger_1.loggerPrefix} Error flushing event to logger: ${error.message}`);
            }
        });
    }
    maybeLogAssignment(result) {
        const { flagKey, format, subjectKey, allocationKey = null, subjectAttributes, variation, flagEvaluationDetails, extraLogging = {}, entityId, } = result;
        const event = {
            ...extraLogging,
            allocation: allocationKey,
            experiment: allocationKey ? `${flagKey}-${allocationKey}` : null,
            featureFlag: flagKey,
            format,
            variation: variation?.key ?? null,
            subject: subjectKey,
            timestamp: new Date().toISOString(),
            subjectAttributes,
            metaData: this.buildLoggerMetadata(),
            evaluationDetails: flagEvaluationDetails,
            entityId,
        };
        if (variation && allocationKey) {
            // If already logged, don't log again
            const hasLoggedAssignment = this.assignmentCache?.has({
                flagKey,
                subjectKey,
                allocationKey,
                variationKey: variation.key,
            });
            if (hasLoggedAssignment) {
                return;
            }
        }
        try {
            if (this.assignmentLogger) {
                this.assignmentLogger.logAssignment(event);
            }
            else {
                // assignment logger may be null while waiting for initialization, queue up events (up to a max)
                // to be flushed when set
                this.assignmentEventsQueue.push(event);
            }
            this.assignmentCache?.set({
                flagKey,
                subjectKey,
                allocationKey: allocationKey ?? '__eppo_no_allocation',
                variationKey: variation?.key ?? '__eppo_no_variation',
            });
        }
        catch (error) {
            application_logger_1.logger.error(`${application_logger_1.loggerPrefix} Error logging assignment event: ${error.message}`);
        }
    }
    buildLoggerMetadata() {
        return {
            obfuscated: this.isObfuscated(this.getConfiguration()),
            sdkLanguage: 'javascript',
            sdkLibVersion: version_1.LIB_VERSION,
        };
    }
    computeBanditsForFlags(config, subjectKey, subjectAttributes, banditActions, flags) {
        const banditResults = {};
        Object.keys(banditActions).forEach((flagKey) => {
            // First, check how the flag evaluated.
            const flagVariation = flags[flagKey];
            if (flagVariation) {
                // Precompute a bandit, if there is one matching this variation.
                const precomputedResult = this.getPrecomputedBandit(config, flagKey, flagVariation.variationValue, subjectKey, subjectAttributes, banditActions[flagKey]);
                if (precomputedResult) {
                    banditResults[flagKey] = precomputedResult;
                }
            }
        });
        return banditResults;
    }
    getPrecomputedBandit(config, flagKey, variationValue, subjectKey, subjectAttributes, banditActions) {
        const bandit = config.getFlagVariationBandit(flagKey, variationValue);
        if (!bandit) {
            return null;
        }
        const result = this.evaluateBanditAction(flagKey, subjectKey, subjectAttributes, banditActions, bandit.modelData);
        return result
            ? {
                banditKey: bandit.banditKey,
                action: result.actionKey,
                actionNumericAttributes: result.actionAttributes.numericAttributes,
                actionCategoricalAttributes: result.actionAttributes.categoricalAttributes,
                actionProbability: result.actionWeight,
                modelVersion: bandit.modelVersion,
                optimalityGap: result.optimalityGap,
            }
            : null;
    }
}
exports.default = EppoClient;
function checkTypeMatch(expectedType, actualType) {
    return expectedType === undefined || actualType === expectedType;
}
function checkValueTypeMatch(expectedType, value) {
    if (expectedType == undefined) {
        return true;
    }
    switch (expectedType) {
        case interfaces_1.VariationType.STRING:
            return typeof value === 'string';
        case interfaces_1.VariationType.BOOLEAN:
            return typeof value === 'boolean';
        case interfaces_1.VariationType.INTEGER:
            return typeof value === 'number' && Number.isInteger(value);
        case interfaces_1.VariationType.NUMERIC:
            return typeof value === 'number';
        case interfaces_1.VariationType.JSON:
            // note: converting to object downstream
            return typeof value === 'string';
        default:
            return false;
    }
}
//# sourceMappingURL=eppo-client.js.map